import { X, Sparkles, Link2 } from 'lucide-react';
import { useState, useEffect } from 'react';

interface AITrendsFeatureCardProps {
  onClose: () => void;
}

export function AITrendsFeatureCard({ onClose }: AITrendsFeatureCardProps) {
  const [isMobileCompact, setIsMobileCompact] = useState(false);

  // Mobile viewport rule: Fit form in viewport with max 883px height on mobile
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 640;
      setIsMobileCompact(isMobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className={`w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
          isMobileCompact ? 'max-h-[883px]' : ''
        }`}
      >
        {/* Header */}
        <div className={`relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 text-white ${
          isMobileCompact ? 'p-4' : 'p-6'
        }`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Sparkles className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold bg-blue-500/30 px-3 py-1 rounded-lg translate-y-3">AI Trends RAG</h2>
          </div>
          <p className="text-white/90 text-sm">
            Real-time intelligence retrieval powered by Jina AI Reader
          </p>
        </div>

        {/* Content */}
        <div className={`space-y-6 max-h-[70vh] overflow-y-auto bg-white ${
          isMobileCompact ? 'p-3 space-y-3' : 'p-6 space-y-6'
        }`}>
          {/* Key Features */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Key Features
            </h3>
            <div className="space-y-3">
              <FeatureItem
                title="Automatic Detection"
                description="Detects AI questions using 25+ keywords"
              />
              <FeatureItem
                title="Parallel Retrieval"
                description="Fetches from both sources simultaneously"
              />
              <FeatureItem
                title="Real-Time Content"
                description="Always fresh, up-to-date AI information"
              />
              <FeatureItem
                title="Graceful Fallback"
                description="Shows source links if retrieval fails"
              />
              <FeatureItem
                title="Non-Blocking"
                description="Doesn't interfere with existing functionality"
              />
              <FeatureItem
                title="Source Attribution"
                description="Always credits Forbes & MIT Tech Review"
              />
            </div>
          </section>

          {/* How It Works */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              How It Works
            </h3>
            
            {/* Priority System */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Priority System
              </h4>
              <div className="space-y-2">
                <PriorityStep number="1" label="Special handlers" secondary="News, Training, etc." />
                <PriorityStep number="2" label="Multi-agent collaboration" />
                <PriorityStep number="3" label="AI Trends RAG" isNew />
                <PriorityStep number="4" label="Keyword topics" />
                <PriorityStep number="5" label="Generic fallback" />
              </div>
            </div>

            {/* RAG Enhancement */}
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-sm font-semibold !text-[#000000] mb-3">
                RAG Enhancement
              </h4>
              <ul className="space-y-2 text-sm !text-[#000000]">
                <li className="flex items-start gap-2">
                  <span className="!text-[#000000] mt-0.5">•</span>
                  <span className="!text-[#000000]">Detects questions about AI trends, companies, innovations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="!text-[#000000] mt-0.5">•</span>
                  <span className="!text-[#000000]">Retrieves real content from configured sources</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="!text-[#000000] mt-0.5">•</span>
                  <span className="!text-[#000000]">Provides context-rich, citation-backed responses</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Sources */}
          <section className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              📊 Configured Sources
            </h4>
            <div className="space-y-2">
              <a
                href="https://www.forbes.com/lists/ai50/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-blue-600 hover:underline"
              >
                → Forbes AI 50
              </a>
              <a
                href="https://www.technologyreview.com/topic/artificial-intelligence/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-blue-600 hover:underline"
              >
                → MIT Technology Review - AI
              </a>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className={`bg-gray-50 border-t border-gray-200 ${
          isMobileCompact ? 'p-2' : 'p-4'
        }`}>
          <button
            onClick={onClose}
            className={`w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg ${
              isMobileCompact ? 'py-2 px-3 text-sm' : 'py-3 px-4'
            }`}
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper Components
interface FeatureItemProps {
  title: string;
  description: string;
}

function FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <div className="flex items-start gap-2 sm:gap-3">
      <div className="mt-0.5 text-green-600 flex-shrink-0">
        <span className="text-base">✅</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm sm:text-sm font-medium text-gray-900">
          {title}
        </div>
        <div className="text-xs text-gray-600">
          {description}
        </div>
      </div>
    </div>
  );
}

interface PriorityStepProps {
  number: string;
  label: string;
  secondary?: string;
  isNew?: boolean;
}

function PriorityStep({ number, label, secondary, isNew }: PriorityStepProps) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
        {number}
      </div>
      <div className="flex-1">
        <span className="text-gray-700 font-medium">
          {label}
        </span>
        {secondary && (
          <span className="text-gray-500 text-xs ml-1">
            ({secondary})
          </span>
        )}
        {isNew && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold rounded-full">
            ⭐ NEW!
          </span>
        )}
      </div>
    </div>
  );
}